import { Mesh, Material } from 'three';
import type { Vec2, AABB } from '../../geo/types';
import { createMeshBuilder, MeshBuilder } from './meshBuilder';
import { CHUNK_SIZE } from '../../core/config';

export type ChunkLayer = 'fabric' | 'infrastructure';

export interface ChunkSink {
  builderAt(layer: ChunkLayer, p: Vec2): MeshBuilder;
}

export interface Chunker extends ChunkSink {
  finalize(material: Material): readonly Mesh[];
}

interface ChunkCell {
  builder: MeshBuilder;
  cx: number;
  cy: number;
  layer: ChunkLayer;
}

export function createChunker(
  bounds: AABB,
  cellSize: Readonly<Record<ChunkLayer, number>> = CHUNK_SIZE
): Chunker {
  const cells = new Map<string, ChunkCell>();

  function cellKey(layer: ChunkLayer, cx: number, cy: number): string {
    return `${layer}:${cx},${cy}`;
  }

  function getCellCoords(layer: ChunkLayer, p: Vec2): { cx: number; cy: number } {
    const size = cellSize[layer];
    return {
      cx: Math.floor((p[0] - bounds.minX) / size),
      cy: Math.floor((p[1] - bounds.minY) / size),
    };
  }

  function builderAt(layer: ChunkLayer, p: Vec2): MeshBuilder {
    const { cx, cy } = getCellCoords(layer, p);
    const key = cellKey(layer, cx, cy);

    let cell = cells.get(key);
    if (!cell) {
      cell = { builder: createMeshBuilder(), cx, cy, layer };
      cells.set(key, cell);
    }

    return cell.builder;
  }

  function finalize(material: Material): readonly Mesh[] {
    const meshes: Mesh[] = [];

    for (const cell of cells.values()) {
      if (cell.builder.vertexCount === 0) continue;

      const geometry = cell.builder.finish();
      const mesh = new Mesh(geometry, material);
      mesh.matrixAutoUpdate = false;
      mesh.frustumCulled = true;
      meshes.push(mesh);
    }

    return meshes;
  }

  return { builderAt, finalize };
}
